// Public-site analytics tracker. Posts events to the same-origin
// /api/v1/events ingestion endpoint. The server reads the session cookie
// (`_ford_analytics_session`, set by Public::AnalyticsSessionConcern), so
// the client doesn't need to attach session_id to the body.
//
// Usage:
//   import { track, trackBatch } from "../lib/analytics"
//   track("feature_view", { feature_id: "..." })
//
// All errors are silently swallowed — analytics must never break the page.

const EVENT_URL = "/api/v1/events"
const BATCH_URL = "/api/v1/events/batch"
export const TRACKING_CONSENT_COOKIE = "_ford_tracking_consent"
export const TRACKING_CONSENT_ACCEPTED = "accepted"
export const TRACKING_CONSENT_DECLINED = "declined"
export const TRACKING_CONSENT_ACCEPTED_EVENT = "ford:tracking-consent:accepted"

function eventId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, marker => {
    const random = Math.floor(Math.random() * 16)
    const value = marker === "x" ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

function buildPayload(eventName, properties) {
  const normalizedProperties = { ...(properties || {}) }
  normalizedProperties.event_id ||= eventId()

  return {
    event_name: eventName,
    occurred_at: new Date().toISOString(),
    path: window.location.pathname + window.location.search,
    referrer: document.referrer || null,
    properties: normalizedProperties
  }
}

function cookieValue(name) {
  const match = document.cookie
    .split("; ")
    .find(row => row.startsWith(`${encodeURIComponent(name)}=`))

  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null
}

export function trackingConsentState() {
  return cookieValue(TRACKING_CONSENT_COOKIE)
}

export function trackingAllowed() {
  return trackingConsentState() === TRACKING_CONSENT_ACCEPTED
}

export async function track(eventName, properties = {}) {
  if (!trackingAllowed()) return

  try {
    await fetch(EVENT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildPayload(eventName, properties)),
      credentials: "include",
      // keepalive lets the request survive page unload — important for
      // media_complete fired during navigation.
      keepalive: true
    })
  } catch (_) { /* silent */ }
}

export async function trackBatch(events) {
  if (!trackingAllowed()) return
  if (!Array.isArray(events) || events.length === 0) return
  const payload = {
    events: events.map(e => buildPayload(e.event_name, e.properties))
  }
  try {
    await fetch(BATCH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
      keepalive: true
    })
  } catch (_) { /* silent */ }
}
